// ---------------------------------------------------------
// Datei: services/substituteResolver.ts
// Zweck: Prüft, ob Pflichtdokumente durch Ersatzdokumente erfüllt werden
// Besonderheiten:
// - Erkennt Ersatzdokumente gemäß der 'substitutedBy'-Konfiguration
// - Aktualisiert den Status eines Dokuments auf 'substituted'
// ---------------------------------------------------------

import { DocumentType } from '../types/documents';

/**
 * Prüft, ob ein erforderliches Dokument durch ein Ersatzdokument erfüllt wird.
 * Ein Dokument gilt als "ersetzt", wenn ein anderes Dokument in der Liste existiert,
 * dessen ID in der 'substitutedBy'-Liste des Originaldokuments enthalten ist,
 * UND dieses Ersatzdokument den Status 'completed' hat.
 * 
 * @param documents Liste aller Dokumente
 * @param documentId ID des zu prüfenden Dokuments
 * @returns true, wenn das Dokument durch ein Ersatzdokument erfüllt wird, sonst false
 */
export const isSubstituted = (documents: DocumentType[], documentId: string): boolean => {
  // Das Zieldokument finden
  const document = documents.find(doc => doc._id === documentId);
  if (!document) return false;
  
  // Prüfen, ob Ersetzungen definiert sind
  if (!document.substitutedBy || !Array.isArray(document.substitutedBy) || document.substitutedBy.length === 0) {
    return false;
  }
  
  // Prüfen, ob eines der Ersatzdokumente hochgeladen wurde
  return document.substitutedBy.some(substituteId => {
    const substitute = documents.find(doc => doc._id === substituteId);
    return substitute?.status === 'completed';
  });
};

/**
 * Aktualisiert den Status aller Dokumente unter Berücksichtigung von Ersatzdokumenten.
 * Wenn ein Pflichtdokument durch ein Ersatzdokument erfüllt wird, erhält es den Status 'substituted'.
 * 
 * @param documents Liste aller Dokumente
 * @returns Aktualisierte Dokumentenliste mit aktualisierten Status
 */
export const resolveSubstitutes = (documents: DocumentType[]): DocumentType[] => {
  // Sicherstellen, dass wir mit einer Kopie arbeiten
  const updatedDocuments = [...documents];
  
  // Alle Pflichtdokumente durchgehen
  updatedDocuments
    .filter(doc => doc.required && doc.status === 'pending' && (doc.substitutedBy?.length ?? 0) > 0)
    .forEach(doc => {
      // Prüfen, ob das Dokument durch ein Ersatzdokument erfüllt wird
      if (isSubstituted(documents, doc._id)) {
        // Status auf 'substituted' setzen
        const docIndex = updatedDocuments.findIndex(d => d._id === doc._id);
        if (docIndex >= 0) {
          updatedDocuments[docIndex] = {
            ...updatedDocuments[docIndex],
            status: 'substituted'
          };
        }
      }
    });
  
  return updatedDocuments;
};

/**
 * Berechnet den Fortschritt (abgeschlossen / erforderlich) unter Berücksichtigung
 * von Ersatzdokumenten.
 * 
 * @param documents Liste aller Dokumente
 * @returns Fortschritt { completed: number, required: number }
 */
export const calculateProgressWithSubstitutes = (documents: DocumentType[]): { completed: number, required: number } => {
  // Zuerst Ersetzungen auflösen
  const resolvedDocuments = resolveSubstitutes(documents);
  
  // Anzahl der Pflichtdokumente ermitteln
  const requiredCount = resolvedDocuments.filter(doc => doc.required).length;
  
  // Anzahl der erledigten Pflichtdokumente ermitteln (completed oder substituted)
  const completedCount = resolvedDocuments.filter(
    doc => doc.required && (doc.status === 'completed' || doc.status === 'substituted')
  ).length;
  
  return { completed: completedCount, required: requiredCount };
};